import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import PoweredBy from '../PoweredBy';

const LululemonTakebackWelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppContext();

  useEffect(() => {
    // Reset state for new takeback session
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const handleStartTakeback = () => {
    navigate('/lululemon/before-start');
  };

  const handleVoiceAssistant = () => {
    // Open voice assistant on phone
    const isLocal = window.location.hostname === 'localhost';
    const voiceBaseUrl = isLocal ? 'http://localhost:3002' : 'https://lululemon.voice.pentatonic.com';
    const sessionUrl = `${voiceBaseUrl}/?session=takeback-${Date.now()}&store=lululemon-shorthills&mode=takeback`;
    window.open(sessionUrl, '_blank');
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#1A1A1A' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, #D31334 0%, transparent 70%)'
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[920px] flex justify-between items-center">
            <img 
              src="/assets/images/logos/lululemon-white.png" 
              alt="lululemon" 
              className="h-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <span className="text-white/60 text-sm uppercase tracking-wider">Like New</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-20">
          {/* Hero Text */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 
              className="text-7xl font-light mb-6"
              style={{ color: '#FFFFFF', fontFamily: "'Calibre', 'Helvetica Neue', sans-serif" }}
            >
              Give Your Gear
            </h1>
            <h1 
              className="text-7xl font-light mb-8"
              style={{ color: '#FFFFFF', fontFamily: "'Calibre', 'Helvetica Neue', sans-serif" }}
            >
              <span style={{ color: '#D31334' }}>New Life</span>
            </h1>
            <p 
              className="text-2xl font-light max-w-2xl mx-auto"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Trade in your gently used lululemon and earn credit towards your next purchase. It's good for you, good for the community, and good for the planet.
            </p>
          </motion.div>

          {/* Value Proposition Cards */}
          <motion.div
            className="grid grid-cols-3 gap-8 max-w-4xl w-full mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <ValueCard
              icon={
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              title="Earn Credit"
              description="Get up to 50% of retail value as store credit"
            />
            <ValueCard
              icon={
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              }
              title="Give Back"
              description="Items are cleaned, restored, and resold affordably"
            />
            <ValueCard
              icon={
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              title="Reduce Waste"
              description="Extend the life of quality gear"
            />
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col items-center gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <button
              onClick={handleStartTakeback}
              className="px-12 py-4 text-lg uppercase tracking-wider font-medium rounded transition-all"
              style={{ 
                backgroundColor: '#D31334',
                color: '#FFFFFF',
              }}
            >
              Start Trade-In
            </button>
            <button
              onClick={handleVoiceAssistant}
              className="text-white/60 hover:text-white text-sm uppercase tracking-wider transition-colors"
            >
              Need Help? Talk to AI Assistant
            </button>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="flex justify-center pb-8">
          <PoweredBy />
        </div>
      </div>
    </motion.div>
  );
};

interface ValueCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const ValueCard: React.FC<ValueCardProps> = ({ icon, title, description }) => (
  <div 
    className="text-center p-6 rounded-2xl"
    style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
  >
    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ color: '#D31334' }}>
      {icon}
    </div>
    <h3 className="text-white text-lg font-medium mb-2">{title}</h3>
    <p className="text-white/60 text-sm">{description}</p>
  </div>
);

export default LululemonTakebackWelcomeView;

